/**
 * salescertNav — compute the next section to route to after a submit.
 * Returns { href, label } | null (when nothing forward is pending → the
 * caller should route to the hub or the report).
 *
 * Rules:
 *  - Forward-only: only sections that come AFTER `justCompleted` in the
 *    canonical order (MCQ → Sales Pitch → Role Play).
 *  - A candidate skips forward past sections that are already `completed`
 *    OR have no attempts left (`attempts_used >= attempts_max`). Otherwise
 *    the "Continue to X" button would land on an X page that immediately
 *    409s with "You've used all attempts" and shows a scary error screen.
 *  - Feb 2026 fix: also treat status="recorded" (embargoed but submitted)
 *    as "no further attempts implied" — those sections may still have
 *    retake room but the participant has already recorded once and doesn't
 *    need to bounce back into them from another section's success screen.
 */
const SECTION_NAV = {
  mcq: { key: "mcq", href: "/participant/salescert/mcq", label: "Knowledge MCQ" },
  sales_pitch: { key: "sales_pitch", href: "/participant/salescert/pitch", label: "Sales Pitch" },
  roleplay: { key: "roleplay", href: "/participant/salescert/roleplay", label: "AI Role Play" },
};

function hasAttemptsLeft(section) {
  if (!section) return true; // status not loaded → optimistically show the button; page will fail-safe.
  const used = section.attempts_used ?? 0;
  const max = section.attempts_max ?? Infinity;
  return used < max;
}

/**
 * Detect "attempts exhausted / already used" errors from a section-page
 * initial load. The backend returns HTTP 409 with one of these details:
 *   • "You've used all N Sales Pitch attempts."
 *   • "You have used all N MCQ attempts. Best score is retained."
 *   • "You have already used all N MCQ attempts."
 *   • "You've used all N Role Play attempts."
 *   • "Your previous role-play attempt was left unfinished and has been forfeited…"
 *   • "Both MCQ attempts were forfeited without submission…"
 *   • "Your Sales Pitch attempt was forfeited. Contact your assessment admin…"
 * All of these are candidate-side "nothing more you can do" states — the
 * hub already surfaces them clearly, so we route back there silently
 * instead of showing "Something went wrong".
 */
export function isAttemptExhaustedError(err) {
  if (!err) return false;
  const status = err?.response?.status;
  if (status !== 409) return false;
  const detail = String(err?.response?.data?.detail || "").toLowerCase();
  if (!detail) return false;
  return (
    detail.includes("used all") ||
    detail.includes("already used") ||
    detail.includes("were forfeited") ||
    detail.includes("was forfeited")
  );
}

export function nextSalescertSection(status, justCompleted) {
  if (!status?.sections) return null;
  const order = ["mcq", "sales_pitch", "roleplay"];
  const idx = order.indexOf(justCompleted);
  if (idx === -1) return null;
  // Forward-only: look at every section strictly AFTER the one just submitted.
  for (const key of order.slice(idx + 1)) {
    const s = status.sections.find((x) => x.key === key);
    if (!s) return SECTION_NAV[key]; // section missing from status → let the target page handle it
    // Skip only when there is genuinely nothing more the candidate can do
    // on that section — either they've already completed AND released, or
    // they've exhausted every attempt. "recorded" (submitted while
    // embargoed but with attempts left) still gets proposed so a candidate
    // who has done attempt 1 of Sales Pitch can be nudged into attempt 2
    // via the next-section chain.
    if (s.status === "completed") continue;
    if (!hasAttemptsLeft(s)) continue;
    return SECTION_NAV[key];
  }
  return null; // all forward sections done or exhausted → caller should route to report / hub
}
